import { every, flattenDeep, toPairs } from 'lodash';

// # Internals

/**
 * take a thing:
 * if it is an array, return that array.
 * if it is not an array, put it in an array.
 *
 * @param someThing - the thing to do it to
 * @returns {array}
 */
const ensureIsArray = (someThing) =>
    Array.isArray(someThing) ? someThing : [someThing];

/**
 * create one function that returns true if all of the provided functions return true
 *
 * @param {array} truthyFunctions
 * @returns {(any) => boolean}
 */
const combineTruthyFunctions = (truthyFunctions) => (value) =>
    every(truthyFunctions, (func) => func(value));

/**
 * combine string values from multiple objects into new object
 *
 * @param {object} previous
 * @param {object} next
 * @returns {object}
 */
// TODO: Refactor copy in @eb/http
export const reduceErrorObjects = (previous, next) => {
    const errors = { ...previous };

    toPairs(next).forEach(([key, value]) => {
        if (value) {
            errors[key] = errors[key] ? `${errors[key]}, ${value}` : value;
        }
    });
    return errors;
};

/*
# Value Validators

Value validators will take a value and return errorMessage or undefined
 */

/**
 * create a single value validator function
 *
 * @param {string} message - error message to display should any of the truthyFunctions fail
 * @param {array} truthyFunctions - functions whose return value truthiness represents the validity of the input value
 * @returns {function}
 */
export const makeValueValidator = (message, ...truthyFunctions) => (value) =>
    combineTruthyFunctions(truthyFunctions)(value) ? undefined : message;

/**
 * create a single value validator function from a configuration object
 *
 * @param {object} config - configuration object (described below)
 * @param {string} config.errorMessage - error message to display should this fail
 * @param {function|array} config.validator - truthy function or an array of them
 * @returns {function}
 *
 * EXAMPLE CONFIGS:
 *
 *    Single validator:
 *      {
 *          errorMessage: "Must be the word FOO",
 *          validator: (valueUnderTest) => ( valueUnderTest === 'FOO' )
 *      }
 *
 *    Multiple Validators:
 *      {
 *          errorMessage: "Must not be FOO or BAR",
 *          validator: [
 *              (valueUnderTest) => ( valueUnderTest !== 'FOO' ),
 *              (valueUnderTest) => ( valueUnderTest !== 'BAR' )
 *          ]
 *      }
 *
 */
export const makeValueValidatorFromConfig = (config) =>
    makeValueValidator(config.errorMessage, ...ensureIsArray(config.validator));

/*
# Object Validators

Object validators will take an object ({key: value})
and return and object ({key: errorMessage or undefined})
 */

/**
 * creates an object validator function from multiple object validator functions
 *
 * @param {...function} validators
 * @returns {function}
 */
export const joinValidators = (...validators) => (values, props) =>
    validators
        .map((validate) => validate(values, props))
        .reduce(reduceErrorObjects);

/**
 * create an object validator function from a configuration object
 *
 * @param {object} config - configuration object (described below)
 * @param {object|array} config.X - single value validator config object or objects
 * @returns {function}
 *
 * EXAMPLE CONFIGS:
 *
 *    single validator for a property:
 *
 *      {
 *          myFavoriteVariableName: {
 *              validator: (valueUnderTest) => ( valueUnderTest !== 'Foo' ),
 *              errorMessage: 'Foo is better'
 *          }
 *      }
 *
 *    multiple validators for a property:
 *
 *      {
 *          theWorstVariableName: [
 *              {
 *                  validator: (valueUnderTest) => ( valueUnderTest === 'Foo' ),
 *                  errorMessage: 'Foo cannot be the worst variable name.'
 *              },
 *              {
 *                  validator: (valueUnderTest) => ( valueUnderTest === 'Bar' ),
 *                  errorMessage: 'What kind of a monster are you?'
 *              }
 *          ]
 *      }
 */
export const makeValidator = (config) => {
    const validators = toPairs(config).map(([fieldName, fieldConfig]) =>
        ensureIsArray(fieldConfig).map((validatorConfig) => {
            const validator = makeValueValidatorFromConfig(validatorConfig);

            return (values) => ({ [fieldName]: validator(values[fieldName]) });
        }),
    );

    const flattenedValidators = flattenDeep(validators);

    return joinValidators(...flattenedValidators);
};
